// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { DocumentData } from "firebase/firestore";

import { OpportunityType } from "~datatypes";
import { Hydratable } from "~hydration-utils";

import { DenialConfirmationModalName } from "../../core/OpportunityDenial/DenialConfirmationModals";
import { StatusPalette } from "../../core/utils/workflowsUtils";
import {
  AutoSnoozeUpdate,
  Denial,
  ExternalRequestUpdate,
  ExternalSystemRequestStatus,
  ManualSnoozeUpdate,
  OfficerApprovalRequest,
  OfficerDenialRequest,
  OfficerRequest,
  SharedSnoozeUpdate,
  Submission,
  SupervisorResponse,
  UpdateLog,
} from "../../FirestoreStore";
import { DistributiveOmit, PartialRecord } from "../../utils/typeUtils";
import { JusticeInvolvedPerson } from "../types";
import { FormBase } from "./Forms/FormBase";
import {
  IApiOpportunityConfiguration,
  OpportunityConfiguration,
} from "./OpportunityConfigurations";
import { SnoozeConfiguration } from "./OpportunityConfigurations/modules/SnoozeConfiguration/interfaces/ISnoozeConfiguration";
import { opportunityConstructors } from "./opportunityConstructors";

export type OpportunityRequirement = {
  isHeading?: boolean;
  text: string;
  tooltip?: string;
  key?: string; // only needed when multiple requirements might have the same text
};

// ranked roughly by actionability
export const OPPORTUNITY_STATUS_RANKED = [
  "PENDING",
  "GRANT_REVIEW", // An officer has requested an opp grant; supervisor has yet to respond
  "SNOOZE_REVIEW", // An officer has requested a snooze of any kind; supervisor has yet to respond
  "GRANT_APPROVED", // A supervisor has approved the opp grant request, but the opp is not yet marked submitted
  "REVISIONS_REQUESTED", // A supervisor requests revisions on a snooze request
  "IN_PROGRESS", // form viewed
  "SUBMITTED", // user marked as "submitted" or "in progress"
  "DENIED",
  "COMPLETED",
  "ALMOST",
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUS_RANKED)[number];
export const PRIORITY_STATUS_RANKED = ["HIGH", "NORMAL"] as const;

export type DenialReasonsMap = Record<string, string>;

/**
 * The settings for a denial reason's text input field
 */
export type DenialInputSettings = {
  required: boolean;
  heading?: string;
  placeholder?: string;
  inputType?: "number" | "text";
  minCharacters?: number;
  maxCharacters?: number;
  prefix?: string;
};

export type DefaultEligibility = "ELIGIBLE" | "MAYBE";

export type OpportunityCaseNote = {
  noteTitle?: string;
  noteBody?: string;
  eventDate?: Date;
};

export type OpportunityNotification =
  IApiOpportunityConfiguration["notifications"][number] & { link?: string };

export type WithCaseNotes = {
  caseNotes: Record<string, OpportunityCaseNote[]>;
};

export type FormVariant = "deferred";

export type ActedOnTextAddition = PartialRecord<OpportunityStatus, string>;

/**
 * Pages that have notifications that are not specific to a single opportunity.
 * Therefore, the notification handlebars cannot be hydrated with the opportunity context.
 */
type GlobalOpportunityNotificationPage = "caseload";
/**
 * A map of notifications by page, excluding the global notification pages.
 */
export type OpportunityNotificationsByPage = PartialRecord<
  Exclude<
    OpportunityNotification["pages"][number],
    GlobalOpportunityNotificationPage
  >,
  OpportunityNotification[]
>;

/**
 * An Opportunity is associated with a single client.
 * The client is assumed to be eligible for the Opportunity unless the
 * `almostEligible` flag is set. There is no "ineligible" flag! Ineligibility
 * must be indicated by the absence of an Opportunity.
 * Common form fields are included as optional, for convenience in cases
 * where form-related behavior is optional. Opportunity-specific extensions of this type
 * should generally override them to be required.
 */
export interface Opportunity<
  PersonType extends JusticeInvolvedPerson = JusticeInvolvedPerson,
> extends Hydratable {
  config: OpportunityConfiguration;
  record: DocumentData | undefined;
  actedOnTextAddition?: ActedOnTextAddition;
  almostEligible: boolean;
  isIneligible: boolean;
  // TODO: move this to status component once almost-eligible is standardized on TES
  almostEligibleStatusMessage?: string;
  almostEligibleRecommendedNote?: { title: string; text: string };
  eligibleStatusMessage?: string;
  person: PersonType;
  readonly defaultEligibility: DefaultEligibility;
  form?: FormBase<any>;
  requirementsAlmostMet: OpportunityRequirement[];
  requirementsMet: OpportunityRequirement[];
  requirementsNotMet: OpportunityRequirement[];
  nonOMSRequirements: OpportunityRequirement[];
  reviewStatus: OpportunityStatus;
  readonly type: OpportunityType;
  previewBannerText?: string;
  denial: Denial | undefined;
  denialReasons: DenialReasonsMap;
  manualSnooze: ManualSnoozeUpdate | undefined;
  autoSnooze: AutoSnoozeUpdate | undefined;
  manualSnoozeUntilDate: Date | undefined;
  snoozedBy: SharedSnoozeUpdate["snoozedBy"] | undefined;
  snoozedOnDate: Date | undefined;
  isSnoozed: boolean;
  snoozeForDays?: number;
  omsSnoozeStatus?: ExternalSystemRequestStatus;
  opportunityDenialViewButtonText?: string;
  deleteOpportunityDenialAndSnooze: () => Promise<void>;
  lastViewed: UpdateLog | undefined;
  setLastViewed: () => void;
  setCompletedIfEligible: () => void;
  almostEligibilityDate: Date | undefined;
  eligibilityDate: Date | undefined;
  readonly supportsExternalRequest: boolean;
  externalRequestData?: ExternalRequestUpdate<any>;
  readonly externalRequestStatusMessage?: string;
  setAutoSnooze: (
    autoSnoozeParams: NonNullable<SnoozeConfiguration["autoSnoozeParams"]>,
    reasons: string[],
  ) => Promise<void>;
  setManualSnooze: (days: number, reasons: string[]) => Promise<void>;
  setDenialReasons: (
    reasons: string[],
    updatedUserInput?: Record<string, string>,
  ) => Promise<void>;
  setOtherReasonText: (otherReason?: string) => Promise<void>;
  trackListViewed: () => void;
  trackPreviewed: () => void;
  readonly deniedTabTitle: string;
  readonly caseNotesTitle?: string;
  readonly hideUnknownCaseNoteDates?: boolean;
  readonly eligibilityCallToActionText?: string;
  readonly subcategory?: string;
  subcategoryHeadingFor: (subcategory: string) => string | undefined;
  readonly subcategoryText?: string;
  tabTitle: (
    category?: OpportunityTabGroup,
    reviewerId?: string,
    // TODO(OBT-53651): Remove once reviewer fields are migrated to stable IDs.
    legacyReviewerId?: string,
  ) => OpportunityTab;
  compare: (other: Opportunity) => number;
  showEligibilityStatus: (component: Component) => boolean;
  readonly denialConfirmationModalName?: DenialConfirmationModalName;
  isSubmitted: boolean;
  isPendingOverdue: boolean;
  isEligibleStaleViewed: boolean;
  readonly submittedTabTitle: string;
  readonly submittedButtonText: string;
  readonly undoSubmittedButtonText: string;
  isInSupervisorReview: boolean;
  isInRevisionsRequested: boolean;
  isInSnoozeReview: boolean;
  isInGrantReview: boolean;
  isIndefinitelySnoozed: boolean;
  readonly supervisorReviewTabTitle: string;
  readonly snoozeReviewStatusMessage: string;
  readonly grantReviewStatusMessage: string;
  markSubmittedAndGenerateToast: (
    newSubcategory?: string | undefined,
    customToast?: string | undefined,
  ) => Promise<string | undefined>;
  deleteSubmitted: () => Promise<void>;
  submittedUpdate: Submission | undefined;
  readonly allSubcategoriesOfSubmitted: string[] | undefined;
  submitMenuOptions: string[] | undefined;
  sentryTrackingId: string | undefined;
  instanceDetails: string | undefined;
  labelAddendum: string | undefined;
  selectId: string;
  firestoreUpdateDocId: string;
  denied: boolean;
  highlightCalloutText: string;
  accordionKey: string;
  customStatusPalette?: StatusPalette;
  sentenceStart?: Date | undefined;
  sentenceExpiration?: Date | undefined;
  snoozeCompanionOpportunities?: Opportunity[];
  requiresRevertConfirmation: boolean;
  revertConfirmationCopy: RevertConfirmationCopy;
  handleAdditionalUndoActions: () => Promise<void>;
  showRevertLinkFallback: boolean;
  caseNoteHeaders: string[];
  generateCaseNoteText?: string;
  notificationsByPage: OpportunityNotificationsByPage | undefined;
  eligibilityStatusLabel: (
    includeReasons?: boolean,
    category?: OpportunityTabGroup,
    reviewerId?: string,
    // TODO(OBT-53651): Remove once reviewer fields are migrated to stable IDs.
    legacyReviewerId?: string,
  ) => string | null;
  maxManualSnoozeDays(denialReasons: string[]): number | undefined;
  defaultManualSnoozeDays(denialReasons: string[]): number | undefined;
  opportunityBannerInfo?: OpportunityBannerInfo;
  bannerInfo?: OpportunityBannerInfo;
  actionHistory: OfficerRequest[] | undefined;
  latestAction: OfficerRequest | undefined;
  setOfficerAction: (
    officerActionParams: OfficerApprovalRequest | OfficerDenialRequest,
  ) => Promise<void>;
  markActionHistoryStale: () => Promise<void>;
  deleteActionHistory: () => Promise<void>;
  setSupervisorResponse: (
    supervisorResponseParams: DistributiveOmit<
      SupervisorResponse,
      "date" | "by"
    >,
  ) => Promise<void>;
  indefiniteDenialReasons: DenialReasonsMap;
  denialViewPrompt: string;
  isGrantApproved: boolean;
  latestTransitionDate: Date | undefined;
  currentReviewerId: string | undefined;
  allUniqueReviewerIds: string[] | undefined;
}

export type Component = "OpportunityModuleHeader" | "OpportunityCapsule";

export type OpportunityFactory<
  OpportunitySubtype extends OpportunityType,
  PersonType extends JusticeInvolvedPerson,
> = (type: OpportunitySubtype, person: PersonType) => Opportunity | undefined;

export type OpportunityTab =
  | "Eligible Now"
  | "Almost Eligible"
  | "Overridden"
  | "Marked Ineligible"
  | "Not Currently Eligible"
  // "Other" should never appear in the actual frontend, but exists to provide a default value
  // for when the record is undefined on the Opportunity class
  | "Other"
  | "Overdue For Hearing"
  | "Missing Review Date"
  | "Upcoming Hearings"
  | "Due this week"
  | `Overdue as of ${string}`
  | "Coming up"
  | "Overdue"
  | "Upcoming"
  | "Due now"
  | "Assessment Complete"
  | "In Progress"
  | "Submitted"
  | "Supervisor Review"
  | "Approved by Supervisor"

  // For US_AZ TPR/DTP opportunities
  | "Fast Trackers"
  | "Pending"

  // For US_MI Custody Level Downgrade
  | "Reduction Pending"

  // For US_AZ Admin Supervision
  | "Eligible per Initial Assessment"
  | "Eligible per ORAS"
  | "Submitted for Supervisor Review"

  // For US_UT Early Termination
  | "Report Due (Other)"
  | "Suitable for Early Termination"
  | "Report Submitted"

  // For US_IA Early Discharge opportunity
  | "Ready for Discharge"
  | "Revisions Requests"
  | "Supervisor Review"
  | "Forms Submitted"

  // For US_IA Supervision Level Downgrade opportunity
  | "Pending Eligibility"

  // For US_ND Transfer to Min Facility
  | "Referral Submitted"
  | "Snoozed"

  // For US_PA
  | "Eligibility Unclear"

  //For US_MI
  | "Eligible for Review"
  | "Almost Eligible for Review"
  | "Needs Re-Screen"
  | "Transfer in Progress"
  | "Needs Review"
  | "Active Alert"
  | "Due"
  | "Not Due"

  // For US_ID CRC
  | "Medical-Ineligible"

  // For US_ID Full Term Discharge
  | "Pending Violation"

  // For US_NC
  | "Submitted for Chief Review"
  | "Approved by Chief"
  | "Submitted to Commission"
  | "Approved"
  | "Partially Approved"
  | "Denied"

  // For US_TX
  | "Awaiting Revisions"
  | "Submitted for Review"
  | "Awaiting Review"
  | "Approved in OIMS"
  | "Past Reviews";

// TODO (OBT-39704) Refactor OpportunityTabGroup to enums
export type OpportunityTabGroup =
  | "ELIGIBILITY STATUS"
  | "REVIEW STATUS"
  | "GENDER"
  | "GENDER - Transgender Only";
// NOTE: Consider changing the key type to string, because the source of truth is what is set in the configs as opposed to the code.
export type OpportunityTabGroups = PartialRecord<
  OpportunityTabGroup,
  Readonly<OpportunityTab[]>
>;

export type OpportunityPriority = "NORMAL" | "HIGH";

export type OpportunityMapping = {
  [K in OpportunityType]?: InstanceType<(typeof opportunityConstructors)[K]>[];
};

export interface OpportunityManagerInterface extends Hydratable {
  opportunities: OpportunityMapping;
  incomingOpportunityTypes: OpportunityType[];
  ineligibleOpportunityTypes: OpportunityType[];
  setSelectedOpportunityTypes: (opportunityTypes: OpportunityType[]) => void;
}

export type RevertConfirmationCopy = {
  headerText: string;
  descriptionText: string;
};

export type OpportunityBannerInfo = {
  previewBannerHeading?: string;
  previewBannerText: string;
  link: string;
  linkText: string;
  onLinkClick: () => void;
};
